import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faStar,
  faVideo,
  faRocket,
  faHourglass,
  IconDefinition,
  faEnvelopeOpenText,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

const TERMS_OF_USE_URL = 'https://www.trueimpact.com/terms-of-use';

const Card: React.FC<{
  title: string;
  icon: IconDefinition;
  items: (string | JSX.Element)[];
}> = ({ icon, title, items }) => (
  <div className="cn-before-begin-card">
    <div className="cn-before-begin-card-title">
      <FontAwesomeIcon icon={icon} />
      <h4>{title}</h4>
    </div>
    <ul>
      {items.map((item, index) => (
        <li key={`${title}-${index}`}>{item}</li>
      ))}
    </ul>
  </div>
);

interface WelcomeContentProps {
  onContinue: () => unknown;
}

const WelcomeContent: React.FC<WelcomeContentProps> = ({ onContinue }) => {
  return (
    <div className="cn-welcome-screen-content">
      <div className="cn-welcome-screen-logos">
        <a
          href="https://www.charitynavigator.org"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/images/cn-logo.png"
            alt="Charity Navigator"
            className="cn-welcome-cn-logo"
          />
        </a>
        <a
          href="https://www.trueimpact.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/images/logo_primary.png"
            alt="True Impact"
            className="cn-welcome-ti-logo"
          />
        </a>
      </div>
      <div className="cn-welcome-main-section">
        <h1>Impact Report Builder</h1>
        <p>
          Creating and publishing an Impact Report helps your nonprofit stand
          out to donors.
        </p>
      </div>
      <div className="cn-welcome-time-section">
        <div>
          <FontAwesomeIcon
            size="2x"
            icon={faHourglass}
            color={COLORS.blackBase}
          />
        </div>
        <div>
          <h2>Complete in about 1&ndash;2 hours</h2>
          <p>
            Most nonprofits complete the builder in one focused session after
            drafting responses in advance.
          </p>
        </div>
      </div>
      <div className="cn-welcome-before-section">
        <div className="cn-welcome-before-section-inner">
          <div className="cn-welcome-before-section-header">
            <h2>
              <span className="cn-welcome-before-check">✓</span> Before you
              begin
            </h2>
            <p>(10&ndash;15 minutes of prep saves time later)</p>
          </div>
          <div className="cn-before-begin-items">
            <Card
              title="Have ready"
              icon={faBook}
              items={[
                <>
                  A specific program to report on{' '}
                  <em>(not an org-wide overview)</em>
                </>,
                'The program manager or lead who knows its impact',
              ]}
            />
            <Card
              title="Draft offline first"
              icon={faEnvelopeOpenText}
              items={[
                <>
                  <a
                    href="/docs/ReportBuilderWorksheet-Fillable_4.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cn-welcome-draft-link"
                  >
                    Download the question set (PDF)
                  </a>{' '}
                  and draft responses before entering the builder
                </>,
                <>
                  The builder is sequential &mdash; drafting offline first
                  prevents having to re-enter responses
                </>,
              ]}
            />
            <Card
              title="Watch"
              icon={faVideo}
              items={[
                <>
                  <a
                    href="https://youtu.be/6JqKZoufnIg"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    5-minute overview video
                  </a>
                </>,
              ]}
            />
          </div>
        </div>
      </div>
      <div className="cn-welcome-worth-time">
        <div className="cn-welcome-worth-time-title">
          <FontAwesomeIcon icon={faStar} size="2x" />
          <h2>Why this is worth your time</h2>
        </div>
        <ul>
          <li>
            Supports a <strong>Complete Profile</strong>
          </li>
          <li>Highlights opportunities for organizational learning</li>
          <li>Improves search visibility</li>
          <li>
            Provides a shareable report to communicate your program&rsquo;s
            impact
          </li>
        </ul>
      </div>
      <div className="cn-welcome-cta-section-wrapper">
        <div className="cn-welcome-cta-section">
          <div className="cn-welcome-cta-section-main">
            <div className="cn-welcome-cta-section-header">
              <div className="cn-welcome-worth-time-title">
                <FontAwesomeIcon icon={faRocket} size="2x" />
                <h2>Ready to get started?</h2>
              </div>
              <p>
                Return here once you&apos;ve drafted your responses offline
                &mdash; you&apos;ll select a program, enter your responses, and
                publish your report.
              </p>
            </div>
            <TrueImpactButton onClick={onContinue} className="cn-welcome-cta">
              Start your Impact Report
            </TrueImpactButton>
          </div>
          <div className="cn-welcome-cta-terms">
            By clicking &ldquo;Start your Impact Report&rdquo;, you agree to
            True Impact&rsquo;s{' '}
            <a
              href={TERMS_OF_USE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </a>
            .
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeContent;
